/* eslint-disable no-unused-vars */
import SearchInput, {
  SearctInputProps,
} from 'controls/search/core/searchInput/searchInput';
import { GetChipFunction } from 'hooks/useSearchControl';
import React, { useEffect, useRef, useState } from 'react';
import { Tags } from 'store/types';
import size from 'lodash-es/size';
import reverse from 'lodash-es/reverse';
import style from './generalSearchLine.module.scss';
import cx from 'classnames';
import { animated, useSpring } from 'react-spring';
import { DURATIONS, MIN_TABLET_WIDTH } from 'utils/utils';
import { useSearchParams } from 'react-router-dom';
import { TransitionGroup } from 'react-transition-group';
import { tagsWrapTransition } from 'components/transitionWrappers/tagsWrapTransition';
import { useViewport } from 'contexts/viewportContext';

const MAX_WIDTH_SCROLLER = 520;

type SearchlineProps = SearctInputProps & {
  getChip: GetChipFunction;
  selected: Tags;
  isOpen: boolean;
  getSpecialChips: () => Array<React.ReactElement>;
};

export default function GeneralSearchLine({
  isDisabled = false,
  setIsOpen,
  setSearch,
  search = '',
  getChip,
  selected,
  isOpen,
  getSpecialChips,
}: SearchlineProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isTouchScroll, setIsTouchScroll] = useState(false);
  const [moveDelta, setMoveDelta] = useState(0);
  const [selectedSize, setSelectedSize] = useState(size(selected));
  const { width } = useViewport();

  let currentWidth = `${MAX_WIDTH_SCROLLER}px`;

  //   const isMobileAdaptiveModeOn = window.innerWidth < MIN_TABLET_WIDTH;
  const isMobileAdaptiveModeOn = width <= MIN_TABLET_WIDTH;

  if (!isOpen && scrollRef.current) {
    const currentMax = scrollRef.current.style.maxWidth;
    scrollRef.current.style.maxWidth = `${MAX_WIDTH_SCROLLER}px`;

    const rect = scrollRef.current.getBoundingClientRect();
    const realWidth = rect.width;
    scrollRef.current.style.maxWidth = currentMax;
    currentWidth =
      realWidth > MAX_WIDTH_SCROLLER ? currentWidth : `${realWidth}px`;
  }

  const anime = useSpring({
    config: { duration: DURATIONS.SEARCH },
    maxWidth: isOpen ? '200px' : currentWidth,
  });

  useEffect(() => {
    const wheelListener = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (scrollRef.current) {
        scrollRef.current.scrollLeft += e.deltaY / 2;
      }
    };

    if (scrollRef.current) {
      scrollRef.current.addEventListener('wheel', wheelListener, {
        passive: false,
      });
    }
    return () => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener('wheel', wheelListener);
      }
    };
  }, [scrollRef.current]);

  useEffect(() => {
    // Блокирует все клики, если произошла прокрутка и сбрасывает состояние
    const clickPreventListener = (e: MouseEvent) => {
      if (moveDelta > 2) {
        e.preventDefault();
        e.stopPropagation();
      }

      setMoveDelta(0);
      setIsTouchScroll(false);
    };

    document.addEventListener('click', clickPreventListener, true);
    return () => {
      document.removeEventListener('click', clickPreventListener, true);
    };
  }, [moveDelta]);

  // Скролл в начало при добавлении
  useEffect(() => {
    if (scrollRef.current && size(selected) > selectedSize) {
      scrollRef.current.scrollLeft = 0;
    }

    setSelectedSize(size(selected));
  }, [selected]);

  const isLiked = useSearchParams()[0].get('only_liked');
  const isUploaded = useSearchParams()[0].get('only_uploaded');
  const isHasSpecial = isLiked || isUploaded;

  return (
    <div className={style.container}>
      <SearchInput
        setIsOpen={setIsOpen}
        isDisabled={isDisabled}
        setSearch={setSearch}
        search={search}
      ></SearchInput>

      {size(selected) || isHasSpecial ? (
        <>
          <div className={style.delimiter}></div>
        </>
      ) : (
        ''
      )}
      {!isMobileAdaptiveModeOn && (
        <animated.div
          style={anime}
          className={cx(
            style.scroller,
            'adaptive-generalSearchLine__scroller',
            'noselect'
          )}
          ref={scrollRef}
          onMouseDown={() => {
            setIsTouchScroll(true);
          }}
          onMouseMove={(e) => {
            if (isTouchScroll && scrollRef.current) {
              scrollRef.current.scrollLeft -= e.movementX;
              setMoveDelta(moveDelta + Math.abs(e.movementX));
            }
          }}
        >
          <TransitionGroup className={style.selected} component="div">
            {getSpecialChips()}
            {reverse(
              tagsWrapTransition({
                tags: selected,
                getChip,
              })
            )}
          </TransitionGroup>
        </animated.div>
      )}
    </div>
  );
}
